import React from 'react';
import { Loader2, Send, ArrowRight, AlertTriangle, Mail, BarChart3 } from 'lucide-react';
import { CampaignPanel } from './CampaignPanel';

export const OutreachTab = ({
  engagement,
  campaigns, campaignsLoading, fetchCampaigns,
  onCreateCampaign,
  selectedCampaign, setSelectedCampaign,
  onActivateCampaign, onPauseCampaign,
  analytics, loadingAnalytics,
  onManageTemplates,
  selectedCompanies,
  pushingLeads, onPushLeads,
  companies,
}) => {
  const outreachCompanies = companies.filter(c => c.outreach_status === 'added_to_campaign');

  return (
    <>
      {/* Engagement Metrics */}
      {engagement && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-2" data-testid="engagement-metrics">
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-1">Total Contacted</p>
            <p className="text-xl font-bold text-[#0A1F44]">{engagement.total_contacted}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-1">Opened</p>
            <p className="text-xl font-bold text-blue-600">{engagement.total_opened}</p>
            <p className="text-[10px] text-gray-400">{engagement.total_contacted > 0 ? ((engagement.total_opened / engagement.total_contacted) * 100).toFixed(1) : 0}% rate</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-1">Replied</p>
            <p className="text-xl font-bold text-green-600">{engagement.total_replied}</p>
            <p className="text-[10px] text-gray-400">{engagement.total_contacted > 0 ? ((engagement.total_replied / engagement.total_contacted) * 100).toFixed(1) : 0}% rate</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-1">Bounced</p>
            <p className="text-xl font-bold text-red-500">{engagement.total_bounced}</p>
            <p className="text-[10px] text-gray-400">{engagement.total_contacted > 0 ? ((engagement.total_bounced / engagement.total_contacted) * 100).toFixed(1) : 0}% rate</p>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <CampaignPanel
            campaigns={campaigns}
            loading={campaignsLoading}
            onRefresh={fetchCampaigns}
            onCreateCampaign={onCreateCampaign}
            selectedCampaign={selectedCampaign}
            setSelectedCampaign={setSelectedCampaign}
            onActivate={onActivateCampaign}
            onPause={onPauseCampaign}
            analytics={analytics}
            loadingAnalytics={loadingAnalytics}
            onManageTemplates={onManageTemplates}
          />
        </div>

        <div className="lg:col-span-2 space-y-4">
          {/* Push Leads Panel */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5" data-testid="push-leads-panel">
            <h3 className="text-sm font-bold text-[#0A1F44] mb-3 flex items-center gap-2">
              <ArrowRight className="h-4 w-4 text-[#D4AF37]" />
              Push Leads to Campaign
            </h3>
            {!selectedCampaign ? (
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <AlertTriangle className="h-5 w-5 text-amber-400 mx-auto mb-1" />
                <p className="text-xs text-gray-500">Select a campaign from the left panel to push leads</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-[#0A1F44]/5 rounded-lg">
                  <Mail className="h-4 w-4 text-[#0A1F44]" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#0A1F44]">{selectedCampaign.name}</p>
                    <p className="text-[10px] text-gray-500">
                      {selectedCompanies.size > 0
                        ? `${selectedCompanies.size} companies selected from pipeline`
                        : 'All enriched companies with email contacts'}
                    </p>
                  </div>
                  <button onClick={onPushLeads} disabled={pushingLeads}
                    className="px-4 py-2 bg-[#D4AF37] text-[#0A1F44] text-xs font-bold rounded-lg hover:bg-[#c5a030] disabled:opacity-50 flex items-center gap-1.5"
                    data-testid="push-leads-btn">
                    {pushingLeads ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    Push Leads
                  </button>
                </div>
                <p className="text-[10px] text-gray-400">
                  Pushes decision-maker contacts (with emails) from {selectedCompanies.size > 0 ? 'selected' : 'all qualified'} companies into the campaign.
                  Go to Pipeline tab to select specific companies.
                </p>
              </div>
            )}
          </div>

          {/* Companies in Outreach */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5" data-testid="outreach-activity">
            <h3 className="text-sm font-bold text-[#0A1F44] mb-3 flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-[#D4AF37]" />
              Companies in Outreach
            </h3>
            {outreachCompanies.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">No companies in outreach yet. Push leads from the pipeline.</p>
            ) : (
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {outreachCompanies.map(c => (
                  <div key={c.company} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="h-6 w-6 bg-indigo-100 rounded flex items-center justify-center">
                        <Send className="h-3 w-3 text-indigo-600" />
                      </div>
                      <span className="text-xs font-medium text-[#0A1F44]">{c.company}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-400">{c.contacts_found || 0} contacts</span>
                      <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-medium rounded-full">In Campaign</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
